import { saveAs } from "file-saver";
import JSZip from "jszip";

import { useEffect, useMemo, useRef } from "react";
import { downloadDocument } from "./v2/requests";
import { type DocumentSummary, type DocumentSummaryVersion } from "./types";
import { DocumentVersionStatus } from "~/config/types";
import i18n from "~/i18n";
import { getNoTierLabel } from "~/helpers/tier";

export interface DownloadableVersion {
  document: DocumentSummary;
  version: DocumentSummaryVersion;
  status: DocumentVersionStatus;
}

const statusOrder = [
  DocumentVersionStatus.published,
  DocumentVersionStatus.draft,
  DocumentVersionStatus.archived,
];

const sanitizeZipName = (name: string) =>
  name.replace(/[\\/:*?"<>|]/g, "-").trim() || "_";

export const Utils = {
  getVersionStatuses: (
    downloads: DownloadableVersion[],
  ): DocumentVersionStatus[] =>
    statusOrder.filter((status) =>
      downloads.some((download) => download.status === status),
    ),

  getDownloadableVersions: (
    documents: DocumentSummary[],
  ): DownloadableVersion[] =>
    documents.flatMap((document) => {
      const downloads: DownloadableVersion[] = [];
      if (document.latestPublishedVersion) {
        downloads.push({
          document,
          version: document.latestPublishedVersion,
          status: DocumentVersionStatus.published,
        });
      }
      if (document.latestVersion.status !== DocumentVersionStatus.published) {
        downloads.push({
          document,
          version: document.latestVersion,
          status: document.latestVersion.status,
        });
      }
      return downloads;
    }),

  saveZipDocuments: async (
    downloads: DownloadableVersion[],
    updateProgress: (value: number) => void,
  ) => {
    const zip = new JSZip();
    const statusLabel: Record<DocumentVersionStatus, string> = {
      [DocumentVersionStatus.draft]: i18n.t(
        ($) => $.commons.document.statuses.draft,
      ),
      [DocumentVersionStatus.published]: i18n.t(
        ($) => $.commons.document.statuses.published,
      ),
      [DocumentVersionStatus.archived]: i18n.t(
        ($) => $.commons.document.statuses.archived,
      ),
    };
    const splitByStatus = Utils.getVersionStatuses(downloads).length > 1;
    let count = 0;

    for (const { document: doc, version, status } of downloads) {
      const blob = await downloadDocument(version.id);

      let folder = zip
        .folder(sanitizeZipName(doc.category.title))
        ?.folder(sanitizeZipName(doc.tier?.title ?? getNoTierLabel(i18n.t)));
      if (splitByStatus) {
        folder = folder
          ?.folder(sanitizeZipName(doc.name))
          ?.folder(statusLabel[status]);
      }
      folder?.file(
        `${sanitizeZipName(version.title)}_${version.id}.${version.fileType}`,
        blob,
      );
      count++;
      updateProgress(count);
    }
    await zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, "documents");
    });
  },
};

/**
 * Debounce a callback function
 */
export function useDebounce<
  Callback extends (...args: Parameters<Callback>) => ReturnType<Callback>,
>(callback: Callback, delay: number) {
  const callbackRef = useRef(callback);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Create a stable debounced function wrapper
  return useMemo(() => {
    return (...args: Parameters<Callback>) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Access ref in the timeout callback, not during render
        callbackRef.current(...args);
      }, delay);
    };
  }, [delay]);
}
